<template>
    <div
        class="moreMarketDisplay notranslate"
        v-bind:class="{
            _show: marketCardShow,
        }"
    >
        <div
            class="moreMarketDisplay_type"
            v-bind:class="{ _isMarketShow: marketCardShow }"
            v-on:click="changeMarketCardShow()"
        >
            <div>
                {{ title }}
                <span v-if="scoreDisplay" class="scoreOrCorner">{{ scoreDisplay }}</span>
            </div>
            <SvgIcon class="rotate" iconName="systemIcon_arrowBottom" />
        </div>
        <TransitionCollapse>
            <div v-show="marketCardShow" class="marketCard-content">
                <MarketCollection
                    v-for="(groupedMarkets, groupName) in groupByDisplayType"
                    v-bind:key="`${title}-${groupName}`"
                    v-bind:markets="groupedMarkets"
                    v-bind:showDisplayTypeName="false"
                    v-bind:match="match"
                    v-bind:displaySection="displaySection"
                />
            </div>
        </TransitionCollapse>
    </div>
</template>

<script lang="ts" setup>
    import groupBy from 'lodash/groupBy';
    import mapValues from 'lodash/mapValues';
    import { storeToRefs } from 'pinia';
    import { computed, onMounted, ref, toRefs, watch } from 'vue';
    import MarketCollection from '@/components/betBuilder/MarketCollection.vue';
    import { formatMarketGroupScore } from '@/components/betBuilder/betBuilderOddsDisplay';
    import TransitionCollapse from '@/components/common/TransitionCollapse.vue';
    import type { IEventResultScore, IMatch } from '@/interface/IMatch';
    import type { IBetBuilderMarket } from '@/interface/betBuilder';
    import { DisplaySection } from '@/interface/enum';
    import { sortBetBuilderMarketsForDisplay } from '@/libraries/oddsApi/betBuilderMarketOrder';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    const props = withDefaults(defineProps<{
        title?: string;
        marketOrder?: number;
        markets: IBetBuilderMarket[];
        isCollapse?: boolean;
        match: IMatch;
        displaySection?: DisplaySection;
        score?: IEventResultScore | null;
    }>(), {
        title: '',
        marketOrder: -1,
        isCollapse: false,
        displaySection: DisplaySection.Unknown,
        score: null,
    });

    const marketCardShow = ref(false);
    const { isCollapse } = toRefs(props);
    const { isBetBuilderDisplayPriorityEnabled } = storeToRefs(useFeatureToggleStore());

    const changeMarketCardShow = () => {
        marketCardShow.value = !marketCardShow.value;
    };

    const groupByDisplayType = computed(() => {
        const grouped = groupBy(props.markets, market => market.displayType);

        return mapValues(grouped, markets => sortBetBuilderMarketsForDisplay(
            markets,
            isBetBuilderDisplayPriorityEnabled.value,
            unsortedMarkets => unsortedMarkets,
        ));
    });
    const scoreDisplay = computed(() => formatMarketGroupScore(props.score));

    onMounted(() => {
        marketCardShow.value = props.marketOrder === 1;
    });

    watch(isCollapse, (newVal) => {
        marketCardShow.value = newVal;
    });
</script>

<style lang="scss" scoped>
.moreMarketDisplay {
    background: $moreMarketDisplay_bg;

    &_type {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.7rem 1rem;
        margin-bottom: 0.1rem;
        background: $moreMarketDisplay_type_bg;
        color: #4b4b4b;
        font-weight: bold;

        .svgIcon {
            width: 1.2em;
            height: 1.2em;
            fill: rgba($moreMarketDisplay_type_color, 0.7);
            transition: transform 0.3s;
        }

        &._isMarketShow .svgIcon {
            transform: rotate(-180deg);
        }
    }

    &:last-child > &_type {
        margin-bottom: 0;
    }

    &_content {
        display: flex;
        flex: 1 1;
        flex-direction: row;
        padding: 0 0.5em 1em 1em;
    }

    &_oddGroup {
        flex: 1;
        text-align: center;

        &:last-child {
            border: none;
        }
    }

    &_columnWrapper {
        display: flex;
        flex-direction: column;
    }

    &_columnName {
        display: flex;
        flex-direction: row;
        justify-content: space-around;
        padding: 0.3em;
        color: $moreMarketDisplay_columnName_color;
        text-align: center;
    }

    &_odd {
        flex-direction: row;
        align-items: center;
        margin: 0.5em 0.5em 0 0;
    }
}

.__live {
    &.moreMarketDisplay {
        background: $moreMarketDisplay_bg_live;

        .moreMarketDisplay_type {
            background: $moreMarketDisplay_type_bg_live;

            .scoreOrCorner {
                color: #ff7777;
            }
        }
    }
}

._rotate_table {
    &.moreMarketDisplay {
        .moreMarketDisplay_columnWrapper {
            flex-direction: row;
        }

        .moreMarketDisplay_columnName {
            flex-direction: column;
            padding-left: 1rem;
            text-align: left;
        }

        .moreMarketDisplay_content {
            flex-direction: column;
        }
    }
}
</style>
